import React, { createContext, useState, useContext, useCallback, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useConfig } from './ConfigContext';
import { useResources } from './ResourceContext';

const EnhancementContext = createContext(null);

export const useEnhancements = () => {
  const context = useContext(EnhancementContext);
  if (!context) {
    throw new Error('useEnhancements must be used within an EnhancementProvider');
  }
  return context;
};

const EXCLUDED_AUTO_LINK_TYPES = [
  'exercise_definition',
  'equipment',
  'location_feature',
  'location',
  'workout_plan',
  'workout_session'
];

export const EnhancementProvider = ({ children }) => {
  const [modalData, setModalData] = useState(null);
  const { enhancements } = useConfig();
  const { resources } = useResources();

  const openDefinitionModal = useCallback((termKey) => {
    const definitionData = enhancements?.definitions?.[termKey];
    if (definitionData) {
      setModalData({ term: definitionData.term, definition: definitionData.definition });
    } else {
      console.warn(`No definition found for term: ${termKey}`);
    }
  }, [enhancements]);

  const closeDefinitionModal = useCallback(() => {
    setModalData(null);
  }, []);

  const enhancedTermsMap = useMemo(() => {
    const map = new Map();
    if (enhancements?.definitions) {
      Object.entries(enhancements.definitions).forEach(([key, def]) => {
        const lookupKey = def.caseSensitive ? key : key.toLowerCase();
        map.set(lookupKey, {
            type: 'definition',
            key: key,
            caseSensitive: !!def.caseSensitive
        });
      });
    }

    const resourceTypesToLink = enhancements?.resourceAutoLinking?.resourceTypes;
    if (resourceTypesToLink && Array.isArray(resourceTypesToLink) && resources.length > 0) {
      resources.forEach(res => {
        const type = res.type || res.resource_type;
        if (EXCLUDED_AUTO_LINK_TYPES.includes(type)) {
            return;
        }
        if (resourceTypesToLink.includes(type) && res.name && typeof res.name === 'string') {
           const name = res.name.trim();
           if (name.length >= 3) {
             const lookupKey = name.toLowerCase();
             if (!map.has(lookupKey)) {
               map.set(lookupKey, {
                 type: 'resource',
                 resource: res,
                 caseSensitive: false
               });
             }
           }
        }
      });
    }
    return map;
  }, [enhancements, resources]);

  const enhancedTermsRegex = useMemo(() => {
    if (enhancedTermsMap.size === 0) return null;
    const keys = Array.from(enhancedTermsMap.keys());
    keys.sort((a, b) => b.length - a.length);
    const escapeRegExp = (string) => string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const pattern = keys.map(escapeRegExp).join('|');
    return new RegExp(`\\b(${pattern})\\b`, 'gi');
  }, [enhancedTermsMap]);

  const value = useMemo(() => ({
    enhancementsConfig: enhancements || {},
    openDefinitionModal,
    enhancedTermsMap,
    enhancedTermsRegex,
    customTags: enhancements?.customTags || {},
    messageAppendages: enhancements?.messageAppendages || [], // ADDED
  }), [enhancements, openDefinitionModal, enhancedTermsMap, enhancedTermsRegex]);

  return (
    <EnhancementContext.Provider value={value}>
      {children}
      {modalData && (
        <Dialog open={true} onClose={closeDefinitionModal} aria-labelledby="definition-dialog-title">
          <DialogTitle id="definition-dialog-title">
            {modalData.term}
            <IconButton aria-label="close" onClick={closeDefinitionModal} sx={{ position: 'absolute', right: 8, top: 8 }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <DialogContentText sx={{ whiteSpace: 'pre-wrap' }}>{modalData.definition}</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDefinitionModal}>Close</Button>
          </DialogActions>
        </Dialog>
      )}
    </EnhancementContext.Provider>
  );
};

EnhancementProvider.propTypes = {
  children: PropTypes.node.isRequired,
};